import React, { useState, useEffect, useRef } from 'react';
import { Check, X, Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '../../../components/new-components/ui/button';
import { Input } from '../../../components/new-components/ui/input';
import { Label } from '../../../components/new-components/ui/label';
import { sandboxOnboardingApi } from '../../../services/sandboxApi';

const MAX_ADMINS = 3;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface SandboxCompanyStepProps {
  companyName: string;
  slug: string;
  adminEmails: string[];
  onCompanyNameChange: (name: string) => void;
  onSlugChange: (slug: string) => void;
  onAdminEmailsChange: (emails: string[]) => void;
  onContinue: () => void;
}

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 30);
}

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{1,28}[a-z0-9]$/;

type SlugStatus = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

export function SandboxCompanyStep({
  companyName,
  slug,
  adminEmails,
  onCompanyNameChange,
  onSlugChange,
  onAdminEmailsChange,
  onContinue,
}: SandboxCompanyStepProps) {
  const [slugStatus, setSlugStatus] = useState<SlugStatus>('idle');
  const [slugTouched, setSlugTouched] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-derive slug from company name (only if user hasn't manually edited)
  const [userEditedSlug, setUserEditedSlug] = useState(false);

  useEffect(() => {
    if (!userEditedSlug && companyName) {
      const derived = toSlug(companyName);
      onSlugChange(derived);
    }
  }, [companyName, userEditedSlug, onSlugChange]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!slug) {
      setSlugStatus('idle');
      return;
    }

    if (!SLUG_PATTERN.test(slug)) {
      setSlugStatus('invalid');
      return;
    }

    setSlugStatus('checking');

    debounceRef.current = setTimeout(async () => {
      try {
        const res = await sandboxOnboardingApi.checkSlug(slug);
        if (res.success && res.data) {
          setSlugStatus(res.data.available ? 'available' : 'taken');
        } else {
          setSlugStatus('idle');
        }
      } catch {
        setSlugStatus('idle');
      }
    }, 500);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [slug]);

  const isNameValid = companyName.trim().length >= 2;
  const isSlugValid = SLUG_PATTERN.test(slug);
  // Admin emails: at least one required, all filled entries must be valid
  const filledAdmins = adminEmails.filter(e => e.trim());
  const allAdminsValid = filledAdmins.length > 0 && filledAdmins.every(e => EMAIL_RE.test(e.trim()));
  const canContinue = isNameValid && isSlugValid && slugStatus === 'available' && allAdminsValid;

  function addAdminRow() {
    if (adminEmails.length < MAX_ADMINS) {
      onAdminEmailsChange([...adminEmails, '']);
    }
  }

  function removeAdminRow(idx: number) {
    const next = adminEmails.filter((_, i) => i !== idx);
    onAdminEmailsChange(next.length > 0 ? next : ['']);
  }

  function updateAdminEmail(idx: number, value: string) {
    const next = [...adminEmails];
    next[idx] = value;
    onAdminEmailsChange(next);
  }

  function handleSlugChange(e: React.ChangeEvent<HTMLInputElement>) {
    setUserEditedSlug(true);
    setSlugTouched(true);
    onSlugChange(e.target.value);
  }

  function handleNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    onCompanyNameChange(e.target.value);
    if (!slugTouched) setUserEditedSlug(false);
  }

  const slugStatusIcon = () => {
    switch (slugStatus) {
      case 'checking':
        return <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />;
      case 'available':
        return <Check className="w-4 h-4 text-green-500" />;
      case 'taken':
      case 'invalid':
        return <X className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const slugStatusText = () => {
    switch (slugStatus) {
      case 'checking':
        return <span className="text-xs text-muted-foreground">Checking availability…</span>;
      case 'available':
        return <span className="text-xs text-green-600">Slug available</span>;
      case 'taken':
        return <span className="text-xs text-red-500">Slug taken — choose another</span>;
      case 'invalid':
        return (
          <span className="text-xs text-red-500">
            Must be 3–30 chars, lowercase letters, numbers and hyphens only, no leading/trailing hyphen
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-semibold text-foreground">Set up your sandbox</h2>
        <p className="mt-1 text-muted-foreground">
          Enter your company details. You can customise branding in the next step.
        </p>
      </div>

      <div className="space-y-5">
        {/* Company Name */}
        <div className="space-y-2">
          <Label htmlFor="company-name">Company / Tenant Name</Label>
          <Input
            id="company-name"
            placeholder="e.g. Acorn Capital"
            value={companyName}
            onChange={handleNameChange}
            className={
              companyName.length > 0 && !isNameValid
                ? 'border-red-400 focus-visible:ring-red-400'
                : ''
            }
          />
          {companyName.length > 0 && !isNameValid && (
            <p className="text-xs text-red-500">Minimum 2 characters required</p>
          )}
        </div>

        {/* Slug */}
        <div className="space-y-2">
          <Label htmlFor="slug">Sandbox URL Slug</Label>
          <div className="relative">
            <Input
              id="slug"
              placeholder="e.g. acorn-capital"
              value={slug}
              onChange={handleSlugChange}
              className={
                slug.length > 0 && (slugStatus === 'taken' || slugStatus === 'invalid')
                  ? 'border-red-400 focus-visible:ring-red-400 pr-8'
                  : slug.length > 0 && slugStatus === 'available'
                  ? 'border-green-400 focus-visible:ring-green-400 pr-8'
                  : 'pr-8'
              }
            />
            <div className="absolute inset-y-0 right-2 flex items-center pointer-events-none">
              {slugStatusIcon()}
            </div>
          </div>
          <div className="flex items-center gap-1">{slugStatusText()}</div>
          <p className="text-xs text-muted-foreground">
            Your sandbox will be accessible at{' '}
            <span className="font-mono">
              {slug ? `app.redex.com/${slug}` : 'app.redex.com/your-slug'}
            </span>
          </p>
        </div>
      </div>

      {/* Admin Emails */}
      <div className="space-y-3">
        <div>
          <Label>Admin Emails <span className="text-xs text-muted-foreground font-normal">(up to {MAX_ADMINS})</span></Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            These people will receive sandbox admin access. They must register using the exact email below.
          </p>
        </div>
        <div className="space-y-2">
          {adminEmails.map((email, idx) => {
            const touched = email.length > 0;
            const valid = EMAIL_RE.test(email.trim());
            return (
              <div key={idx} className="flex items-center gap-2">
                <Input
                  type="email"
                  placeholder={`admin${idx + 1}@company.com`}
                  value={email}
                  onChange={e => updateAdminEmail(idx, e.target.value)}
                  className={touched && !valid ? 'border-red-400 focus-visible:ring-red-400' : ''}
                />
                {adminEmails.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeAdminRow(idx)}
                    className="text-muted-foreground hover:text-red-500 transition-colors"
                    aria-label="Remove"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {adminEmails.length < MAX_ADMINS && (
          <button
            type="button"
            onClick={addAdminRow}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Plus className="w-3.5 h-3.5" /> Add another admin
          </button>
        )}
      </div>

      <Button onClick={onContinue} disabled={!canContinue} className="w-full">
        Continue
      </Button>
    </div>
  );
}
